import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  useLayoutEffect,
} from "react";

import { isSameDay, parseISO, format } from "date-fns";
import openSocket from "../../services/socket-io";
import clsx from "clsx";

import { green } from "@material-ui/core/colors";
import {
  Button,
  CircularProgress,
  Divider,
  IconButton,
  makeStyles,
  Tooltip,
} from "@material-ui/core";
import {
  AccessTime,
  Block,
  Description,
  Done,
  DoneAll,
  ExpandMore,
  GetApp,
  Replay,
} from "@material-ui/icons";

import MarkdownWrapper from "../MarkdownWrapper";
import VcardPreview from "../VcardPreview";
import LocationPreview from "../LocationPreview";
import ModalImageCors from "../ModalImageCors";
import MessageOptionsMenu from "../MessageOptionsMenu";
import whatsBackground from "../../assets/wa-background.png";
import {
  getDeliveryProblem,
  isNearConversationEnd,
  mergeMessages,
} from "./presentation";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import Audio from "../Audio";
import MessageHistorySearch from "../MessageHistorySearch";
import LinkPreview, { getLinkPreviewData } from "../LinkPreview";

const useStyles = makeStyles((theme) => ({
  messagesListWrapper: {
    overflow: "hidden",
    minHeight: 0,
    fontFamily: theme.typography.fontFamily,
    position: "relative",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
  },

  messagesList: {
    backgroundImage: `url(${whatsBackground})`,
    minHeight: 0,
    overflowAnchor: "none",
    display: "flex",
    flexDirection: "column",
    flexGrow: 1,
    padding: "24px clamp(12px, 3vw, 40px)",
    overflowY: "auto",
    [theme.breakpoints.down("sm")]: {
      paddingBottom: "90px",
    },
    ...theme.scrollbarStyles,
  },

  circleLoading: {
    color: "#0035a2",
    position: "absolute",
    opacity: "70%",
    top: 0,
    left: "50%",
    marginTop: 12,
  },

  messageLeft: {
    marginRight: 20,
    marginTop: 6,
    minWidth: 112,
    maxWidth: "min(82%, 560px)",
    boxSizing: "border-box",
    fontSize: 15,
    lineHeight: 1.5,
    "& audio": { width: "100%", maxWidth: 320 },
    [theme.breakpoints.down("sm")]: { maxWidth: "94%" },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover $messageActionsButton, &:focus-within $messageActionsButton": {
      opacity: 1,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#ffffff",
    color: "#303030",
    alignSelf: "flex-start",
    borderTopLeftRadius: 0,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    padding: "10px 12px 6px",
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
  },

  quotedContainerLeft: {
    margin: "0 0 8px",
    minWidth: 0,
    overflow: "hidden",
    backgroundColor: "#f0f0f0",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsg: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    display: "block",
    whiteSpace: "pre-wrap",
    overflow: "hidden",
  },

  quotedSideColorLeft: {
    flex: "none",
    width: "4px",
    backgroundColor: "#6bcbef",
  },

  messageRight: {
    marginLeft: 20,
    marginTop: 6,
    minWidth: 112,
    maxWidth: "min(82%, 560px)",
    boxSizing: "border-box",
    fontSize: 15,
    lineHeight: 1.5,
    "& audio": { width: "100%", maxWidth: 320 },
    [theme.breakpoints.down("sm")]: { maxWidth: "94%" },
    height: "auto",
    display: "block",
    position: "relative",
    "&:hover $messageActionsButton, &:focus-within $messageActionsButton": {
      opacity: 1,
    },

    whiteSpace: "pre-wrap",
    backgroundColor: "#e6f4ed",
    color: "#303030",
    alignSelf: "flex-end",
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 0,
    padding: "10px 12px 6px",
    borderRadius: 14,
    boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
  },

  messageTextOnly: {
    minWidth: "min(180px, 100%)",
  },

  searchMessageHighlight: {
    boxShadow: "0 0 0 2px rgba(37, 99, 235, 0.42), 0 2px 8px rgba(15, 23, 42, 0.12)",
  },

  messagePending: {
    opacity: 0.94,
  },

  quotedContainerRight: {
    margin: "0 0 8px",
    minWidth: 0,
    overflowY: "hidden",
    backgroundColor: "#cfe9ba",
    borderRadius: "7.5px",
    display: "flex",
    position: "relative",
  },

  quotedMsgRight: {
    padding: 10,
    maxWidth: 300,
    height: "auto",
    whiteSpace: "pre-wrap",
  },

  quotedSideColorRight: {
    flex: "none",
    width: "4px",
    backgroundColor: "#35cd96",
  },

  messageActionsButton: {
    display: "inline-flex",
    float: "right",
    width: 28,
    height: 28,
    margin: "-4px -4px 0 6px",
    position: "relative",
    color: "#52616b",
    zIndex: 1,
    backgroundColor: "inherit",
    opacity: 0.6,
    "@media (hover: none)": { opacity: 1 },
    "&:focus-visible": { outline: "2px solid #2563eb", outlineOffset: 2 },
    "&:hover, &.Mui-focusVisible": { backgroundColor: "inherit" },
  },

  messageContactName: {
    display: "flex",
    color: "#6bcbef",
    fontWeight: 500,
  },
  outgoingAuthor: {
    display: "flex",
    flexDirection: "column",
    padding: "0 0 6px",
    lineHeight: 1.2,
  },
  outgoingAuthorName: {
    color: "#2f7d32",
    fontSize: 12,
    fontWeight: 600,
  },
  outgoingAuthorQueue: {
    color: "#6b6b6b",
    fontSize: 11,
  },

  textContentItem: {
    overflowWrap: "anywhere",
    padding: 0,
    minHeight: 22,
    "& a": { color: "#1758a6", textDecoration: "underline" },
  },

  textContentItemDeleted: {
    fontStyle: "italic",
    color: "#626b73",
    overflowWrap: "break-word",
    padding: 0,
  },

  messageMedia: {
    objectFit: "cover",
    width: "100%",
    maxWidth: 340,
    maxHeight: 420,
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
    borderBottomLeftRadius: 8,
    borderBottomRightRadius: 8,
    display: "block",
  },
  mediaPreview: {
    marginTop: 2,
    marginBottom: 2,
    borderRadius: 8,
    overflow: "hidden",
    maxWidth: 340,
    backgroundColor: "#00000008",
  },
  documentPreview: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: 12,
    backgroundColor: "#fff",
  },
  documentInfo: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  documentName: {
    fontSize: 14,
    fontWeight: 500,
    overflowWrap: "anywhere",
  },
  documentType: {
    fontSize: 12,
    color: "#666",
  },

  timestamp: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    flexWrap: "wrap",
    gap: 4,
    clear: "both",
    marginTop: 6,
    fontSize: 12,
    lineHeight: 1.4,
    color: "#596772",
  },

  dailyTimestamp: {
    alignItems: "center",
    textAlign: "center",
    alignSelf: "center",
    width: "110px",
    backgroundColor: "#ffffff",
    margin: "10px",
    borderRadius: "10px",
    boxShadow: "0 1px 1px #b3b3b3",
  },

  dailyTimestampText: {
    color: "#52616b",
    fontSize: 12,
    fontWeight: 500,
    padding: 8,
    alignSelf: "center",
    marginLeft: "0px",
  },

  internalNote: {
    alignSelf: "center",
    maxWidth: 520,
    margin: "10px 0",
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "#fff4c2",
    color: "#4f4630",
    fontSize: 12,
    lineHeight: 1.45,
    boxShadow: "0 1px 1px #b3b3b3",
    whiteSpace: "pre-wrap",
  },

  internalNoteTime: {
    display: "block",
    marginTop: 4,
    color: "#8a7f5a",
    fontSize: 10,
    textAlign: "right",
  },

  ackIcons: {
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },
  ackStatusText: {
    marginLeft: 4,
    fontSize: 12,
    fontWeight: 600,
    verticalAlign: "middle",
  },
  ackFailedText: {
    color: theme.palette.error.main,
  },
  ackPendingText: {
    color: "#8a6d1d",
  },

  deletedIcon: {
    fontSize: 18,
    verticalAlign: "middle",
    marginRight: 4,
  },

  ackDoneAllIcon: {
    color: green[500],
    fontSize: 18,
    verticalAlign: "middle",
    marginLeft: 4,
  },

  downloadMedia: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: "inherit",
    padding: 10,
  },
  // ===== NOVOS ESTILOS =====
  messagesContent: {
    display: "flex",
    flexDirection: "column",
    minWidth: 0,
  },
  unreadDivider: {
    alignSelf: "stretch",
    textAlign: "center",
    margin: "20px 0",
    padding: "8px 12px",
    borderRadius: 8,
    backgroundColor: "#e8efff",
    color: "#244c93",
    fontSize: 12,
    fontWeight: 600,
  },
  jumpToLatest: {
    position: "absolute",
    right: 20,
    bottom: 20,
    zIndex: 5,
    borderRadius: 24,
    backgroundColor: "#ffffff",
    color: "#244c93",
    boxShadow: "0 3px 16px rgba(15,23,42,0.16)",
    padding: "10px 16px",
    textTransform: "none",
    "&:hover": { backgroundColor: "#edf3ff" },
  },
  deliveryProblem: {
    clear: "both",
    marginTop: 10,
    padding: "10px 12px",
    borderRadius: 8,
    backgroundColor: "#fff0ef",
    color: "#8c2722",
    fontSize: 13,
    lineHeight: 1.5,
    whiteSpace: "normal",
    border: "1px solid #efc9c5",
    "& strong": { display: "block", marginBottom: 2 },
    "& .MuiButton-root": {
      marginTop: 6,
      textTransform: "none",
      color: "inherit",
    },
  },
  uncertainDelivery: {
    backgroundColor: "#fff8e6",
    color: "#705014",
    borderColor: "#ecdbb1",
  },
  emptyState: {
    margin: "auto",
    padding: 32,
    textAlign: "center",
    color: "#596772",
    fontSize: 14,
  },
  sticker: {
    width: 160,
    height: 160,
    objectFit: "contain",
    cursor: "pointer",
    backgroundColor: "transparent",
    display: "block",
  },
  stickerMessageLeft: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,
    minWidth: "auto",
  },
  stickerMessageRight: {
    backgroundColor: "transparent",
    boxShadow: "none",
    padding: 0,
    minWidth: "auto",
  },

  stickerModalOverlay: {
    position: "fixed",
    top: 0,
    left: 0,
    width: "100vw",
    height: "100vh",
    backgroundColor: "rgba(0,0,0,0.85)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },

  stickerModalImage: {
    maxWidth: "90vw",
    maxHeight: "90vh",
    objectFit: "contain",
  },
}));

const reducer = (state, action) => {
  switch (action.type) {
    case "LOAD_MESSAGES":
      return mergeMessages(state, action.payload);
    case "ADD_MESSAGE":
    case "UPDATE_MESSAGE":
      return mergeMessages(state, [action.payload]);
    case "DELETE_MESSAGE":
      return state.filter((message) => message.id !== action.payload);
    case "RESET":
      return [];
    default:
      return state;
  }
};

const MessagesList = ({ ticketId, isGroup, pendingMessages = [] }) => {
  const classes = useStyles();

  const [messagesList, dispatch] = useReducer(reducer, []);
  const [pageNumber, setPageNumber] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const listRef = useRef(null);
  const contentRef = useRef(null);
  const nearBottomRef = useRef(true);
  const scrollPlanRef = useRef(null);
  const fetchingRef = useRef(false);
  const historyCursorRef = useRef(null);
  const knownIdsRef = useRef(new Set());
  const loadedMessagesPageRef = useRef(0);
  const unreadIdsRef = useRef(new Set());
  const initializedRef = useRef(false);
  const [awayFromBottom, setAwayFromBottom] = useState(false);
  const [newMessageCount, setNewMessageCount] = useState(0);
  const [firstUnreadId, setFirstUnreadId] = useState(null);
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [retryingMessageIds, setRetryingMessageIds] = useState(new Set());
  const [searchFocusId, setSearchFocusId] = useState(null);
  const newMessageLabel =
    newMessageCount === 1
      ? "1 nova mensagem"
      : `${newMessageCount} novas mensagens`;

  const [selectedMessage, setSelectedMessage] = useState({});
  const [anchorEl, setAnchorEl] = useState(null);
  const messageOptionsMenuOpen = Boolean(anchorEl);
  const currentTicketId = useRef(ticketId);

  const updateHistoryCursor = (pageMessages = []) => {
    const oldestMessage = pageMessages[0];
    if (!oldestMessage?.createdAt || !oldestMessage?.id) return;

    const currentCursor = historyCursorRef.current;
    if (!currentCursor) {
      historyCursorRef.current = oldestMessage;
      return;
    }

    const candidateTime = new Date(oldestMessage.createdAt).getTime();
    const currentTime = new Date(currentCursor.createdAt).getTime();
    if (
      candidateTime < currentTime ||
      (candidateTime === currentTime &&
        String(oldestMessage.id).localeCompare(String(currentCursor.id)) < 0)
    ) {
      historyCursorRef.current = oldestMessage;
    }
  };

  const handleRetryMessage = async (message) => {
    if (retryingMessageIds.has(message.id)) return;
    setRetryingMessageIds((current) => new Set(current).add(message.id));
    try {
      await api.post(
        `/messages/${encodeURIComponent(message.id)}/retry`,
      );
    } catch (error) {
      toastError(error);
    } finally {
      setRetryingMessageIds((current) => {
        const next = new Set(current);
        next.delete(message.id);
        return next;
      });
    }
  };

  const [stickerModalOpen, setStickerModalOpen] = useState(false);
  const [stickerUrl, setStickerUrl] = useState(null);
  const normalizeComparableBody = (value) =>
    String(value || "").replace(/^\u200e+/, "");

  const isSameOutgoingMessage = (pendingMessage, sentMessage) => {
    if (!pendingMessage?.fromMe || !sentMessage?.fromMe) return false;

    const sameQuotedMessage =
      (pendingMessage.quotedMsg?.id || null) ===
      (sentMessage.quotedMsg?.id || null);

    if (pendingMessage.mediaType || sentMessage.mediaType) {
      return (
        sameQuotedMessage &&
        pendingMessage.mediaType &&
        pendingMessage.mediaType === sentMessage.mediaType
      );
    }

    return (
      sameQuotedMessage &&
      normalizeComparableBody(pendingMessage.body) ===
        normalizeComparableBody(sentMessage.body)
    );
  };

  const visiblePendingMessages = pendingMessages.filter(
    (pendingMessage) =>
      !messagesList.some((message) =>
        isSameOutgoingMessage(pendingMessage, message),
      ),
  );

  const displayedMessages = [...messagesList, ...visiblePendingMessages].sort(
    (a, b) => new Date(a.createdAt) - new Date(b.createdAt),
  );

  const handleOpenSticker = (url) => {
    setStickerUrl(url);
    setStickerModalOpen(true);
  };

  const handleCloseSticker = () => {
    setStickerModalOpen(false);
    setStickerUrl(null);
  };

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
    setHasMore(false);
    knownIdsRef.current = new Set();
    historyCursorRef.current = null;
    loadedMessagesPageRef.current = 0;
    unreadIdsRef.current = new Set();
    setSearchFocusId(null);
    nearBottomRef.current = true;
    initializedRef.current = false;
    fetchingRef.current = false;
    scrollPlanRef.current = { type: "bottom" };
    setAwayFromBottom(false);
    setNewMessageCount(0);
    setFirstUnreadId(null);
    currentTicketId.current = ticketId;
  }, [ticketId]);

  useEffect(() => {
    setLoading(true);
    let active = true;
    const delayDebounceFn = setTimeout(() => {
      const fetchMessages = async () => {
        try {
          if (pageNumber > 1 && listRef.current && scrollPlanRef.current?.type !== "search") {
            scrollPlanRef.current = {
              type: "history",
              height: listRef.current.scrollHeight,
              top: listRef.current.scrollTop,
            };
          }
          const firstPage = loadedMessagesPageRef.current + 1;
          for (let requestedPage = firstPage; requestedPage <= pageNumber; requestedPage += 1) {
            const cursor = historyCursorRef.current;
            const params =
              requestedPage > 1 && cursor
                ? {
                    pageNumber: requestedPage,
                    beforeCreatedAt: cursor.createdAt,
                    beforeId: cursor.id,
                  }
                : { pageNumber: requestedPage };
            const { data } = await api.get("/messages/" + ticketId, { params });
            if (!active || currentTicketId.current !== ticketId) return;
            if (requestedPage === 1 && !initializedRef.current && scrollPlanRef.current?.type !== "search") {
              initializedRef.current = true;
              const firstUnread = data.messages.find(
                (message) => !message.fromMe && !message.read,
              );
              setFirstUnreadId((id) => id || firstUnread?.id || null);
              scrollPlanRef.current = firstUnread
                ? { type: "unread", id: firstUnread.id }
                : { type: "bottom" };
            }
            updateHistoryCursor(data.messages);
            data.messages.forEach((message) => knownIdsRef.current.add(message.id));
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
            loadedMessagesPageRef.current = requestedPage;
            setHasMore(data.hasMore);
          }
          setLoading(false);
          fetchingRef.current = false;
        } catch (err) {
          if (!active) return;
          setLoading(false);
          fetchingRef.current = false;
          toastError(err);
        }
      };
      fetchMessages();
    }, 500);
    return () => {
      active = false;
      clearTimeout(delayDebounceFn);
      fetchingRef.current = false;
    };
  }, [pageNumber, ticketId]);

  useEffect(() => {
    const socket = openSocket();

    let active = true;
    socket.on("connect", () => {
      socket.emit("joinChatBox", ticketId, (joined) => {
        if (!active || !joined?.ok) return;
        // Join first, then fetch the latest page. Events arriving during this
        // request are merged by id, closing the disconnect/reconnect gap.
        api
          .get(`/messages/${ticketId}`, { params: { pageNumber: 1 } })
          .then(({ data }) => {
            if (!active) return;
            updateHistoryCursor(data.messages);
            data.messages.forEach((message) => {
              trackNewMessage(message);
              knownIdsRef.current.add(message.id);
            });
            setHasMore(data.hasMore);
            dispatch({ type: "LOAD_MESSAGES", payload: data.messages });
          })
          .catch((error) => {
            if (active) toastError(error);
          });
      });
    });

    socket.on("appMessage", (data) => {
      if (data.message && String(data.message.ticketId) !== String(ticketId))
        return;
      if (data.action === "create") {
        trackNewMessage(data.message);
        knownIdsRef.current.add(data.message.id);
        dispatch({ type: "ADD_MESSAGE", payload: data.message });
      }

      if (data.action === "update") {
        dispatch({ type: "UPDATE_MESSAGE", payload: data.message });
      }

      if (data.action === "delete") {
        unreadIdsRef.current.delete(data.messageId);
        setNewMessageCount(unreadIdsRef.current.size);
        dispatch({ type: "DELETE_MESSAGE", payload: data.messageId });
      }
    });

    return () => {
      active = false;
      socket.disconnect();
    };
  }, [ticketId]);

  const clearNewMessages = () => {
    unreadIdsRef.current.clear();
    setNewMessageCount(0);
  };

  const trackNewMessage = (message) => {
    if (
      knownIdsRef.current.has(message.id) ||
      message.fromMe ||
      nearBottomRef.current
    )
      return;
    unreadIdsRef.current.add(message.id);
    setNewMessageCount(unreadIdsRef.current.size);
    setFirstUnreadId((id) => id || message.id);
  };

  const scrollToBottom = () => {
    const list = listRef.current;
    if (!list) return;
    nearBottomRef.current = true;
    list.scrollTop = list.scrollHeight;
    setAwayFromBottom(false);
    clearNewMessages();
  };

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    const plan = scrollPlanRef.current;
    if (plan?.type === "search") {
      const target = Array.from(list.querySelectorAll("[data-message-id]")).find(
        (element) => element.dataset.messageId === String(plan.id),
      );
      if (!target) {
        nearBottomRef.current = false;
        setAwayFromBottom(true);
        return;
      }
      scrollPlanRef.current = null;
      list.scrollTop +=
        target.getBoundingClientRect().top -
        list.getBoundingClientRect().top -
        (list.clientHeight - target.clientHeight) / 2;
    } else {
      scrollPlanRef.current = null;
      if (plan?.type === "history") {
        list.scrollTop = plan.top + list.scrollHeight - plan.height;
      } else if (plan?.type === "unread") {
        const target = Array.from(list.querySelectorAll("[data-message-id]")).find(
          (element) => element.dataset.messageId === String(plan.id),
        );
        if (target)
          list.scrollTop +=
            target.getBoundingClientRect().top -
            list.getBoundingClientRect().top -
            48;
      } else if (plan?.type === "bottom" || nearBottomRef.current) {
        scrollToBottom();
      }
    }
    nearBottomRef.current = isNearConversationEnd(list);
    setAwayFromBottom(!nearBottomRef.current);
  }, [messagesList, pendingMessages]);

  useEffect(() => {
    if (typeof ResizeObserver === "undefined" || !contentRef.current) return;
    const observer = new ResizeObserver(() => {
      if (nearBottomRef.current) scrollToBottom();
    });
    observer.observe(contentRef.current);
    return () => observer.disconnect();
  }, [ticketId]);

  const handleScroll = (event) => {
    const list = event.currentTarget;
    const atBottom = isNearConversationEnd(list);
    nearBottomRef.current = atBottom;
    setAwayFromBottom(!atBottom);
    if (atBottom) clearNewMessages();
    if (list.scrollTop < 50 && hasMore && !loading && !fetchingRef.current) {
      fetchingRef.current = true;
      setPageNumber((page) => page + 1);
    }
  };

  const handleSearchResultSelect = ({ message, historyPage }) => {
    const scrollToSearchMessage = () => {
      const list = listRef.current;
      if (!list) return;
      const target = Array.from(list.querySelectorAll("[data-message-id]")).find(
        (element) => element.dataset.messageId === String(message.id),
      );
      if (!target) return;
      list.scrollTop +=
        target.getBoundingClientRect().top -
        list.getBoundingClientRect().top -
        (list.clientHeight - target.clientHeight) / 2;
      nearBottomRef.current = isNearConversationEnd(list);
      setAwayFromBottom(!nearBottomRef.current);
    };
    setSearchFocusId(String(message.id));
    const alreadyRendered = Array.from(
      listRef.current?.querySelectorAll("[data-message-id]") || [],
    ).some((element) => element.dataset.messageId === String(message.id));
    if (alreadyRendered || loadedMessagesPageRef.current >= historyPage) {
      requestAnimationFrame(scrollToSearchMessage);
      return;
    }
    nearBottomRef.current = false;
    setAwayFromBottom(true);
    scrollPlanRef.current = { type: "search", id: String(message.id) };
    setPageNumber(historyPage);
  };

  const handleOpenMessageOptionsMenu = (e, message) => {
    setAnchorEl(e.currentTarget);
    setSelectedMessage(message);
  };

  const handleCloseMessageOptionsMenu = (e) => {
    setAnchorEl(null);
  };

  const isStickerMessage = (message) =>
    /\.webp(\?|$)/i.test(message.mediaUrl || "");

  const parseLegacyOutgoingBody = (body) => {
    const text = String(body || "");
    const match = text.match(
      /^\*{1,2}([^*\n:]+(?:\s*\([^)\n]+\))?)\s*:\*{1,2}\s*\n([\s\S]*)$/,
    );

    if (!match) {
      return { author: "", cleanBody: text };
    }

    return {
      author: match[1].trim(),
      cleanBody: match[2],
    };
  };

  const getDisplayBody = (message) => {
    if (!message.fromMe) return message.body;
    return parseLegacyOutgoingBody(message.body).cleanBody;
  };

  const getOutgoingAuthor = (message) => {
    if (!message.fromMe) return null;

    const legacyAuthor = parseLegacyOutgoingBody(message.body).author;
    const name = message.user?.name || legacyAuthor;
    const queue = message.queue?.name;

    if (!name && !queue) return null;

    return { name, queue };
  };

  const shouldRenderMessageBody = (message) => {
    if (message.isDeleted) return true;
    if (!message.mediaUrl) return true;
    if (message.mediaType === "location" || message.mediaType === "vcard") {
      return true;
    }
    return false;
  };

  const renderLinkPreview = (message) => {
    if (message.mediaUrl || message.isDeleted) return null;

    const preview = getLinkPreviewData(getDisplayBody(message));
    if (!preview) return null;

    return (
      <LinkPreview
        url={preview.url}
        title={preview.title}
        subtitle={preview.subtitle}
      />
    );
  };

  const checkMessageMedia = (message) => {
    if (
      message.mediaType === "location" &&
      message.body.split("|").length >= 2
    ) {
      let locationParts = message.body.split("|");
      let imageLocation = locationParts[0];
      let linkLocation = locationParts[1];

      let descriptionLocation = null;

      if (locationParts.length > 2)
        descriptionLocation = message.body.split("|")[2];

      return (
        <LocationPreview
          image={imageLocation}
          link={linkLocation}
          description={descriptionLocation}
        />
      );
    } else if (/\.webp$/i.test(message.mediaUrl)) {
      return (
        <img
          src={message.mediaUrl}
          alt="sticker"
          className={classes.sticker}
          onClick={() => handleOpenSticker(message.mediaUrl)}
          draggable={false}
        />
      );
    } else if (message.mediaType === "vcard") {
      let array = message.body.split("\n");
      let obj = [];
      let contact = "";
      for (let index = 0; index < array.length; index++) {
        const v = array[index];
        let values = v.split(":");
        for (let ind = 0; ind < values.length; ind++) {
          if (values[ind].indexOf("+") !== -1) {
            obj.push({ number: values[ind] });
          }
          if (values[ind].indexOf("FN") !== -1) {
            contact = values[ind + 1];
          }
        }
      }
      return <VcardPreview contact={contact} numbers={obj[0]?.number} />;
    } else if (
      /^.*\.(jpe?g|png|gif)?$/i.exec(message.mediaUrl) &&
      message.mediaType === "image"
    ) {
      return (
        <div className={classes.mediaPreview}>
          <ModalImageCors imageUrl={message.mediaUrl} />
        </div>
      );
    } else if (message.mediaType === "audio") {
      return <Audio url={message.mediaUrl} />;
    } else if (message.mediaType === "video") {
      return (
        <div className={classes.mediaPreview}>
          <video
            className={classes.messageMedia}
            src={message.mediaUrl}
            controls
          />
        </div>
      );
    } else {
      return (
        <>
          <div className={classes.mediaPreview}>
            <div className={classes.documentPreview}>
              <Description color="action" />
              <div className={classes.documentInfo}>
                <span className={classes.documentName}>
                  {message.body || "Arquivo"}
                </span>
                <span className={classes.documentType}>Anexo</span>
              </div>
            </div>
          </div>

          <div className={classes.downloadMedia}>
            <Button
              startIcon={<GetApp />}
              color="primary"
              variant="outlined"
              target="_blank"
              href={message.mediaUrl}
            >
              BAIXAR MÍDIA
            </Button>
          </div>
          <Divider />
        </>
      );
    }
  };

  const renderMessageAck = (message) => {
    if (message.sendStatus === "uncertain") {
      return <span className={classes.ackStatusText}>Sem confirmação</span>;
    }
    if (message.ack === -1 || message.sendStatus === "failed") {
      return (
        <Tooltip title="Falhou. Verifique a instância ou tente reenviar.">
          <span>
            <Block
              color="error"
              fontSize="small"
              className={classes.ackIcons}
            />
            <span
              className={clsx(classes.ackStatusText, classes.ackFailedText)}
            >
              Falhou
            </span>
          </span>
        </Tooltip>
      );
    }
    if (message.sendStatus === "pending" || message.isPending) {
      return (
        <Tooltip title="Mensagem na fila de envio">
          <span aria-label="Mensagem na fila de envio">
            <AccessTime fontSize="small" className={classes.ackIcons} />
          </span>
        </Tooltip>
      );
    }
    if (message.sendStatus === "sending") {
      return (
        <Tooltip title="Enviando mensagem">
          <span style={{ display: "inline-flex", marginLeft: 4 }}>
            <CircularProgress size={14} />
          </span>
        </Tooltip>
      );
    }
    if (message.ack === 0) {
      return (
        <Tooltip title="Aguardando confirmacao do WhatsApp">
          <span>
            <AccessTime fontSize="small" className={classes.ackIcons} />
          </span>
        </Tooltip>
      );
    }
    if (message.ack === 1) {
      return (
        <Tooltip title="Enviado para o WhatsApp.">
          <Done fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 2) {
      return (
        <Tooltip title="Entregue ao destinatário.">
          <DoneAll fontSize="small" className={classes.ackIcons} />
        </Tooltip>
      );
    }
    if (message.ack === 3 || message.ack === 4) {
      return (
        <Tooltip title="Lido pelo destinatário.">
          <DoneAll fontSize="small" className={classes.ackDoneAllIcon} />
        </Tooltip>
      );
    }
  };

  const renderDailyTimestamps = (message, index) => {
    if (index === 0) {
      return (
        <span
          className={classes.dailyTimestamp}
          key={`timestamp-${message.id}`}
        >
          <div className={classes.dailyTimestampText}>
            {format(parseISO(displayedMessages[index].createdAt), "dd/MM/yyyy")}
          </div>
        </span>
      );
    }
    if (index > 0) {
      let messageDay = parseISO(displayedMessages[index].createdAt);
      let previousMessageDay = parseISO(displayedMessages[index - 1].createdAt);

      if (!isSameDay(messageDay, previousMessageDay)) {
        return (
          <span
            className={classes.dailyTimestamp}
            key={`timestamp-${message.id}`}
          >
            <div className={classes.dailyTimestampText}>
              {format(
                parseISO(displayedMessages[index].createdAt),
                "dd/MM/yyyy",
              )}
            </div>
          </span>
        );
      }
    }
    return null;
  };

  const renderMessageDivider = (message, index) => {
    if (index < displayedMessages.length && index > 0) {
      let messageUser = displayedMessages[index].fromMe;
      let previousMessageUser = displayedMessages[index - 1].fromMe;

      if (messageUser !== previousMessageUser) {
        return (
          <span style={{ marginTop: 16 }} key={`divider-${message.id}`}></span>
        );
      }
    }
  };

  const renderQuotedMessage = (message) => {
    return (
      <div
        className={clsx(classes.quotedContainerLeft, {
          [classes.quotedContainerRight]: message.fromMe,
        })}
      >
        <span
          className={clsx(classes.quotedSideColorLeft, {
            [classes.quotedSideColorRight]: message.quotedMsg?.fromMe,
          })}
        ></span>
        <div className={classes.quotedMsg}>
          {!message.quotedMsg?.fromMe && (
            <span className={classes.messageContactName}>
              {message.quotedMsg?.contact?.name}
            </span>
          )}
          {getDisplayBody(message.quotedMsg)}
        </div>
      </div>
    );
  };

  const renderUnreadDivider = (message) =>
    message.id === firstUnreadId && (
      <div
        className={classes.unreadDivider}
        role="separator"
        aria-label="Novas mensagens"
      >
        Novas mensagens
      </div>
    );

  const renderDeliveryProblem = (message) => {
    const problem = getDeliveryProblem(message);
    if (!problem) return null;
    return (
      <div
        className={clsx(classes.deliveryProblem, {
          [classes.uncertainDelivery]: problem.uncertain,
        })}
        role="status"
      >
        <strong>{problem.title}</strong>
        <span>{problem.description}</span>
        {!problem.uncertain &&
          (message.sendStatus === "failed" ||
            (!message.sendStatus && message.ack === -1)) && (
            <div>
              <Button
                size="small"
                startIcon={
                  retryingMessageIds.has(message.id) ? (
                    <CircularProgress size={14} />
                  ) : (
                    <Replay />
                  )
                }
                onClick={() => handleRetryMessage(message)}
                disabled={retryingMessageIds.has(message.id)}
                aria-label="Tentar enviar novamente"
              >
                Tentar novamente
              </Button>
            </div>
          )}
        {!problem.uncertain &&
          !message.mediaUrl &&
          (!message.mediaType || message.mediaType === "chat") && (
            <div>
              <Button
                size="small"
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(
                      getDisplayBody(message) || "",
                    );
                    setCopiedMessageId(message.id);
                  } catch (error) {
                    toastError(error);
                  }
                }}
              >
                {copiedMessageId === message.id
                  ? "Texto copiado"
                  : "Copiar texto"}
              </Button>
            </div>
          )}
      </div>
    );
  };

  const renderMessages = () => {
    if (displayedMessages.length > 0) {
      const viewMessagesList = displayedMessages.map((message, index) => {
        if (message.isInternal) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderUnreadDivider(message)}
              <div
                className={clsx(classes.internalNote, {
                  [classes.searchMessageHighlight]:
                    searchFocusId === String(message.id),
                })}
                data-message-id={message.id}
              >
                <MarkdownWrapper>{message.body}</MarkdownWrapper>
                <span className={classes.internalNoteTime}>
                  {format(parseISO(message.createdAt), "HH:mm")}
                </span>
              </div>
            </React.Fragment>
          );
        }

        if (!message.fromMe) {
          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderUnreadDivider(message)}
              {renderMessageDivider(message, index)}
              {stickerModalOpen && (
                <div
                  className={classes.stickerModalOverlay}
                  onClick={handleCloseSticker}
                >
                  <img
                    src={stickerUrl}
                    alt="sticker-modal"
                    className={classes.stickerModalImage}
                  />
                </div>
              )}
              <div
                data-message-id={message.id}
                className={clsx(classes.messageLeft, {
                  [classes.stickerMessageLeft]: isStickerMessage(message),
                  [classes.searchMessageHighlight]:
                    searchFocusId === String(message.id),
                  [classes.messageTextOnly]:
                    !message.mediaUrl &&
                    !["location", "vcard"].includes(message.mediaType) &&
                    !isStickerMessage(message),
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  aria-label="Opções da mensagem"
                  aria-haspopup="menu"
                  disabled={
                    message.isDeleted ||
                    message.isPending ||
                    String(message.id).startsWith("pending-")
                  }
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {isGroup && (
                  <span className={classes.messageContactName}>
                    {message.contact?.name}
                  </span>
                )}
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                <div className={classes.textContentItem}>
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {shouldRenderMessageBody(message) && (
                    <MarkdownWrapper>{getDisplayBody(message)}</MarkdownWrapper>
                  )}
                  {renderLinkPreview(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        } else {
          const outgoingAuthor = getOutgoingAuthor(message);

          return (
            <React.Fragment key={message.id}>
              {renderDailyTimestamps(message, index)}
              {renderUnreadDivider(message)}
              {renderMessageDivider(message, index)}
              <div
                data-message-id={message.id}
                className={clsx(classes.messageRight, {
                  [classes.stickerMessageRight]: isStickerMessage(message),
                  [classes.messagePending]: message.isPending,
                  [classes.searchMessageHighlight]:
                    searchFocusId === String(message.id),
                  [classes.messageTextOnly]:
                    !message.mediaUrl &&
                    !["location", "vcard"].includes(message.mediaType) &&
                    !isStickerMessage(message),
                })}
              >
                <IconButton
                  variant="contained"
                  size="small"
                  aria-label="Opções da mensagem"
                  aria-haspopup="menu"
                  disabled={
                    message.isDeleted ||
                    message.isPending ||
                    String(message.id).startsWith("pending-")
                  }
                  className={classes.messageActionsButton}
                  onClick={(e) => handleOpenMessageOptionsMenu(e, message)}
                >
                  <ExpandMore />
                </IconButton>
                {(message.mediaUrl ||
                  message.mediaType === "location" ||
                  message.mediaType === "vcard") &&
                  //|| message.mediaType === "multi_vcard"
                  checkMessageMedia(message)}
                {outgoingAuthor && !isStickerMessage(message) && (
                  <div className={classes.outgoingAuthor}>
                    {outgoingAuthor.name && (
                      <span className={classes.outgoingAuthorName}>
                        {outgoingAuthor.name}
                      </span>
                    )}
                    {outgoingAuthor.queue && (
                      <span className={classes.outgoingAuthorQueue}>
                        {outgoingAuthor.queue}
                      </span>
                    )}
                  </div>
                )}
                <div
                  className={clsx(classes.textContentItem, {
                    [classes.textContentItemDeleted]: message.isDeleted,
                  })}
                >
                  {message.isDeleted && (
                    <Block
                      color="disabled"
                      fontSize="small"
                      className={classes.deletedIcon}
                    />
                  )}
                  {message.quotedMsg && renderQuotedMessage(message)}
                  {shouldRenderMessageBody(message) && (
                    <MarkdownWrapper>{getDisplayBody(message)}</MarkdownWrapper>
                  )}
                  {renderLinkPreview(message)}
                  {renderDeliveryProblem(message)}
                  <span className={classes.timestamp}>
                    {format(parseISO(message.createdAt), "HH:mm")}
                    {renderMessageAck(message)}
                  </span>
                </div>
              </div>
            </React.Fragment>
          );
        }
      });
      return viewMessagesList;
    } else {
      return loading ? null : (
        <div className={classes.emptyState}>
          Nenhuma mensagem nesta conversa. Envie uma mensagem para começar.
        </div>
      );
    }
  };

  return (
    <div className={classes.messagesListWrapper}>
      <MessageOptionsMenu
        message={selectedMessage}
        anchorEl={anchorEl}
        menuOpen={messageOptionsMenuOpen}
        handleClose={handleCloseMessageOptionsMenu}
      />
      <MessageHistorySearch
        ticketId={ticketId}
        onSelectResult={handleSearchResultSelect}
      />
      <div
        id="messagesList"
        ref={listRef}
        role="region"
        aria-label="Histórico da conversa"
        tabIndex={0}
        className={classes.messagesList}
        onScroll={handleScroll}
      >
        <div ref={contentRef} className={classes.messagesContent}>
          {renderMessages()}
        </div>
      </div>
      {awayFromBottom && (
        <Button
          className={classes.jumpToLatest}
          onClick={scrollToBottom}
          startIcon={<ExpandMore />}
          aria-label={
            newMessageCount
              ? `Ir para o final: ${newMessageLabel}`
              : "Ir para o final da conversa"
          }
        >
          <span aria-live="polite" aria-atomic="true">
            {newMessageCount ? newMessageLabel : "Ir para o final"}
          </span>
        </Button>
      )}
      {loading && (
        <div>
          <CircularProgress className={classes.circleLoading} />
        </div>
      )}
    </div>
  );
};

export default MessagesList;
